/**
 * What each subscription state means to a dietitian, as opposed to what it
 * means to the gateway.
 *
 * `SubscriptionStatus` is Razorpay's vocabulary, kept as-is so that this app,
 * the webhooks and a support ticket all say the same word. Nobody running a
 * nutrition practice should ever read "HALTED", though, so the translation
 * lives here — one place, because the status card, the result screen and any
 * banner that comes later all have to agree about what a state is called and
 * how alarming it is.
 */

import type {
  AccessSource,
  SubscriptionStatus,
} from "./types/subscriptions.types";

/**
 * How much attention a state deserves.
 *
 * - `good` — running, nothing to do.
 * - `waiting` — something is in flight and will resolve itself.
 * - `attention` — the dietitian has to act or lose access. This is the one
 *   that matters: a halted subscription is where subscription revenue quietly
 *   leaks, and it must not look like a neutral piece of information.
 * - `over` — finished, by choice or otherwise.
 */
export type SubscriptionTone = "good" | "waiting" | "attention" | "over";

export interface SubscriptionFace {
  /** What the pill says. */
  label: string;
  tone: SubscriptionTone;
  /** The line under the plan name, in the dietitian's terms. */
  detail: string;
}

const FACES: Record<SubscriptionStatus, SubscriptionFace> = {
  CREATED: {
    label: "Not started",
    tone: "waiting",
    detail: "This subscription has not been authorised yet.",
  },
  AUTHENTICATED: {
    label: "Starting",
    tone: "waiting",
    detail: "The mandate is in place. Your first payment is being taken.",
  },
  ACTIVE: {
    label: "Active",
    tone: "good",
    detail: "Everything is unlocked.",
  },
  PENDING: {
    label: "Payment retrying",
    tone: "attention",
    detail:
      "A renewal payment did not go through. We are retrying it - check the account behind your mandate has funds.",
  },
  HALTED: {
    label: "Payment failed",
    tone: "attention",
    detail:
      "We could not collect your renewal after several attempts, so the subscription has stopped. Set up the mandate again to carry on.",
  },
  CANCELLED: {
    label: "Cancelled",
    tone: "over",
    detail: "This subscription will not renew.",
  },
  COMPLETED: {
    label: "Finished",
    tone: "over",
    detail: "This subscription has run its course.",
  },
  EXPIRED: {
    label: "Expired",
    tone: "over",
    detail: "The mandate was never authorised, so the subscription lapsed.",
  },
};

export const faceOf = (status: SubscriptionStatus): SubscriptionFace =>
  FACES[status];

/**
 * Whether this workspace's product is unlocked right now.
 *
 * Read off `access_source` and nothing else. It used to be worked out here
 * from the subscription's status and its refund — which states count, whether
 * money on its way back closes the cycle — and every one of those rules is the
 * server's to hold, not this app's: it is the server that refuses the write,
 * and a browser with its own idea of which statuses open the product is a
 * browser that shuts a paying workspace the day the two disagree.
 *
 * Only an explicit `"NONE"` shuts it. `null` is an answer that did not say —
 * an older backend, or a source this build has no name for — and not knowing
 * is not the same as knowing they have not paid.
 *
 * The one place the question is decided, because four screens ask it and none
 * of them may disagree — the gated create buttons, the price list's active
 * badge, the dashboard's checklist, and the plan named under the account in
 * the rail.
 */
export const hasAccess = (source: AccessSource | null): boolean =>
  source !== "NONE";

/** The pill's colours per tone. Attention borrows the same red the app uses
 *  for a failed payment elsewhere, so the two read as the same severity. */
export const TONE_CLASSES: Record<SubscriptionTone, string> = {
  good: "bg-brand-soft text-brand-dark",
  waiting: "bg-[#FFF6E5] text-[#9A6700]",
  attention: "bg-[#FDECEC] text-[#D14343]",
  over: "bg-[#F1F4F6] text-ink-muted",
};

/** How a subscription is charged, written out. */
export const METHOD_LABELS: Record<string, string> = {
  UPI: "UPI Autopay",
  EMANDATE: "Bank eMandate",
  CARD: "Card",
};
